'use client';

import { type ProductRelatedPlant } from '@/domain/plant/types';
import { getProductPlants } from '@/domain/products/api';
import { Chip } from '@andritzot/metris-web-ui/data-display/chip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import { useQuery } from '@tanstack/react-query';

type PlantsDataTableProps = {
  productId: number;
};

const plantTemplate = (dataRow: ProductRelatedPlant) => (
  <Typography component="span">
    {dataRow.plant} {dataRow.confidential && <Chip color="warning" size="small" label="Confidential" />}
  </Typography>
);

const columns: GridColDef<ProductRelatedPlant>[] = [
  {
    field: 'plant',
    headerName: 'Plant',
    flex: 1,
    renderCell: (params) => plantTemplate(params.row),
  },
  {
    field: 'industry.industry',
    headerName: 'Industry',
    flex: 1,
    valueGetter: (_value, row) => row.industry?.industry ?? '',
  },
  {
    field: 'country.name',
    headerName: 'Country',
    flex: 1,
    valueGetter: (_value, row) => row.country?.name ?? '',
  },
];

const getRowId = (row: ProductRelatedPlant) => row.idPlant;

const PlantsDataTable = ({ productId }: PlantsDataTableProps) => {
  const { data: plants, isLoading } = useQuery({
    queryKey: ['plants-for-product', productId],
    queryFn: () => getProductPlants(productId),
  });

  return (
    <DataGrid
      columns={columns}
      rows={plants ?? []}
      getRowId={getRowId}
      loading={isLoading}
      pagination
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      pageSizeOptions={[5, 10, 25, 50, 100]}
    />
  );
};

export default PlantsDataTable;
